import { GridLink } from '@components/gridLink/GridLink';
import { shallow } from 'zustand/shallow';
import { QuoteGetResponse } from '@lio/api-types';
import { Grid, Typography, Box } from '@mui/material';
import { Dialog } from '@components/dialog/Dialog';
import { useStore } from '@store/GlobalStore';
import Loading from '@components/Loading';
import { Dictionary } from 'lodash';
import { useQuote } from '@services/quotes';
import { Table } from '@components/table/Table';
import { TableGridBaseColDef } from '@components/table/types/TableColumnDef.type';
import { useCustomerCoverageCandidate } from '../../../../src/services/customer/useCustomerCoverageCandidate';

interface IDuplicateQuoteDialog {
  showDialog: boolean;
  quote?: QuoteGetResponse;
  quoteId?: string;
  closeLinkDialog: () => void;
  isUpdateQuoteLoading: boolean;
}

let duplicateQuoteItems: TableGridBaseColDef[] = [
  {
    field: 'Quote',
    headerName: 'Quote',
    flex: 2,
    sortable: false,
    valueGetter: (value, row) => row.quote ?? '-',
    renderCell: ({ row, value }) => <GridLink label={value} to={`/quote/${row.quoteId}`} target='_blank' />,
  },
  {
    field: 'Product',
    headerName: 'Line(s) of Insurance',
    flex: 2,
    sortable: false,
    valueGetter: (value, row) => row.product ?? '-',
  },
  {
    field: 'Brokerage',
    headerName: 'Brokerage',
    flex: 2,
    sortable: false,
    valueGetter: (value, row) => row.brokerage ?? '-',
  },
];

export const DuplicateQuoteDialog = ({
  showDialog,
  quote,
  quoteId,
  closeLinkDialog,
  isUpdateQuoteLoading,
}: IDuplicateQuoteDialog) => {
  const { quote: currentQuote } = useQuote(!quote ? quoteId : undefined);
  const { isInternalUser } = useStore((state) => state.userAuth, shallow);
  const { isLoading, data } = useCustomerCoverageCandidate(showDialog && quoteId ? quoteId : undefined);
  let duplicateQuotes: any[] = [];

  if (data && data.items && data.items.length > 0) {
    let duplicateQuoteRows: any[] = [];
    let quoteLabels: Dictionary<string[]> = {};

    for (let coverage = 0; coverage < data?.items.length; coverage++) {
      const coverageItem = data?.items[coverage];
      for (let i = 0; i < coverageItem.customerCoverageCandidateQuotes.length; i++) {
        const item = coverageItem.customerCoverageCandidateQuotes[i];

        if (!quoteLabels[item?.quote?.id]) quoteLabels[item?.quote?.id] = [];
        quoteLabels[item?.quote?.id].push(coverageItem?.coverageType.label);

        let existingRow = duplicateQuoteRows.find((el) => el.id === item?.quote?.id);

        if (!existingRow) {
          duplicateQuoteRows.push({
            sectionHeader: false,
            id: item?.quote?.id,
            title: 'title',
            value: 'value',
            quoteId: item?.quote?.id,
            quote: item?.quote?.policyNumber?.policyNumber ?? '-',
            brokerage: item?.quote?.brokerage?.name ?? '-',
          });
        }
      }
    }

    duplicateQuoteRows.forEach((row) => {
      row.product = quoteLabels[row.quoteId].join(', ') ?? ' - ';
    });
    duplicateQuotes = duplicateQuoteRows;
  }

  const selectedQuote = duplicateQuotes.find((dq) => dq.id === quoteId);
  const duplicateQuotesToShow = duplicateQuotes.filter((dq) => dq.id !== quoteId);

  return (
    <Dialog
      open={showDialog}
      title={
        `Duplicate(s) of #` + currentQuote?.policyNumber?.policyNumber + ' (' + currentQuote?.brokerage?.name + ')'
      }
      showBorders={false}
      isCloseButtonDisabled={isUpdateQuoteLoading}
      handleCloseDialog={closeLinkDialog}
      isPrimaryBtnDisabled={false}
      isPrimaryBtnLoading={isUpdateQuoteLoading}
      secondaryBtnText='Exit'
      secondaryBtnAction={closeLinkDialog}
      isSecondaryBtnDisabled={isUpdateQuoteLoading}
      size='medium'
    >
      <Grid
        size={{ xs: 20 }}
        sx={{
          paddingLeft: 2,
          paddingRight: 2,
          margin: '0 auto',
          '.MuiFormControl-root': {
            width: '100%',
          },
        }}
      >
        <Typography variant='bodySmall'>This quote has the following Line(s) of Insurance: </Typography>
        <Typography variant='bodySmall' fontWeight={600}>
          {selectedQuote?.product}
        </Typography>
        <Typography sx={{ display: 'block', mt: '8px' }} variant='bodySmall'>
          The following quotes are flagged as duplicates:
        </Typography>
        {isInternalUser && isLoading ? (
          <Loading />
        ) : (
          <>
            {isInternalUser ? (
              <Box>
                <Table
                  columns={duplicateQuoteItems}
                  ariaLabel='duplicate-quotes'
                  onLoadData={() => ({
                    data: { items: duplicateQuotesToShow ?? [] },
                    pageLimit: 1,
                    pageOffset: 0,
                    isLoading: false,
                    isFetching: false,
                    isError: false,
                  })}
                  paginationConfig={{
                    rowsPerPage: duplicateQuotesToShow?.length,
                    pageOffset: 0,
                    currentPage: 0,
                  }}
                  hidePagination
                />
              </Box>
            ) : null}
          </>
        )}
      </Grid>
    </Dialog>
  );
};
